import { Col } from 'antd';
import { FieldValidator, FORM_ERROR } from 'final-form';
import { omit } from 'lodash';
import { FC } from 'react';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { ChangePasswordValues } from '~src/services/api/base';
import { defaultBaseApi } from '~src/services/ApiController';
import { validatorMinValue6, validatorRequired, validatorTrimmed } from '~src/services/validators';
import BigSuccess from '~src/shared/BigSuccess';
import SecondaryButton from '~src/shared/buttons/basicButtons/SecondaryButton.sc';
import PrimaryButtonLoading from '~src/shared/buttons/loadingButtons/PrimaryButtonLoading';
import CustomFormInput from '~src/shared/form/inputs/FormInput';
import {
  ActionRow,
  ButtonSection,
  Footer,
  FormErrors,
  SectionBody,
  SectionHeader,
  SubSection,
} from '~src/shared/form/shared.sc';
import { composeValidators } from '~src/shared/reactFinalForm/validators';
import { TileContainer } from '~src/styles/containers.sc';
import { FieldType } from '~src/types';

interface ChangePasswordFormValues extends ChangePasswordValues {
  oldPassword: string;
  passwordConfirm: string;
}

const ChangePassword: FC = () => {
  const { t } = useTranslation();

  const validateSamePassword: FieldValidator<string> = (confirmValue: string, allValues?: object) =>
    allValues && (allValues as ChangePasswordFormValues).password !== confirmValue
      ? 'password_confirmation_invalid'
      : undefined;

  const renderInput = ({ autoFocus, input, labelKey, meta: { error, touched } }: FieldType) => {
    return (
      <CustomFormInput
        {...input}
        autoFocus={autoFocus}
        type="password"
        error={touched && error ? error : null}
        label={t(labelKey)}
        labelKey={labelKey}
      />
    );
  };

  const onSubmit = (formValues: ChangePasswordFormValues) => {
    return defaultBaseApi
      .passwordSet(omit(formValues, 'passwordConfirm'))
      .then(() => {
        return;
      })
      .catch(axiosError => {
        let errorMessage = 'unknown error';
        if (axiosError.response.data && axiosError.response.data.error) {
          errorMessage = t(`x.error.${axiosError.response.data.error}`);
        }
        return {
          [FORM_ERROR]: errorMessage,
        };
      });
  };

  return (
    <>
      <Form onSubmit={onSubmit} initialValues={{ oldPassword: '', password: '', passwordConfirm: '' }}>
        {({ handleSubmit, submitError, form, pristine, submitting, submitSucceeded }) => {
          const reset = () => form.reset();

          return submitSucceeded ? (
            <TileContainer>
              <SectionHeader>{t('x.profile_settings.password_updated_message')}</SectionHeader>
              <SectionBody>
                <BigSuccess text="" />
              </SectionBody>
            </TileContainer>
          ) : (
            <TileContainer>
              <form onSubmit={handleSubmit} autoComplete="off">
                <SectionHeader data-translation="x.profile_settings.password.title">
                  {t('x.profile_settings.password.title')}
                </SectionHeader>
                <SectionBody>
                  <SubSection>
                    <Col lg={8}>
                      <Field
                        name="oldPassword"
                        type="password"
                        validate={validatorRequired}
                        render={renderInput}
                        labelKey="x.profile_settings.password.old_password"
                      />
                    </Col>
                  </SubSection>
                  <SubSection>
                    <Col lg={8}>
                      <Field
                        name="password"
                        type="password"
                        validate={composeValidators([
                          validatorRequired,
                          validatorMinValue6,
                          validatorTrimmed,
                        ])}
                        render={renderInput}
                        labelKey="x.profile_settings.password.password"
                      />
                    </Col>
                    <Col lg={8}>
                      <Field
                        name="passwordConfirm"
                        type="password"
                        render={renderInput}
                        validate={validateSamePassword}
                        validateFields={['password']}
                        labelKey="x.profile_settings.password.passwordConfirm"
                      />
                    </Col>
                  </SubSection>
                </SectionBody>
                {submitError && <FormErrors>{submitError}</FormErrors>}
                {!pristine && (
                  <Footer>
                    <ActionRow>
                      <ButtonSection>
                        <SecondaryButton onClick={reset} type="reset">
                          <span data-translation="x.button.cancel">{t('x.button.cancel')}</span>
                        </SecondaryButton>
                        <PrimaryButtonLoading isLoading={submitting}>
                          <span data-translation="x.button.save">{t('x.button.save')}</span>
                        </PrimaryButtonLoading>
                      </ButtonSection>
                    </ActionRow>
                  </Footer>
                )}
              </form>
            </TileContainer>
          );
        }}
      </Form>
    </>
  );
};

export default ChangePassword;
